import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { StatusPill } from '@/components/StatusPill';
import {
  TabBody, TabToolbar, Switch, SkeletonCards, InlineHint, formatDateTime,
} from '@/components/tiktok/measurementShared';
import {
  tiktokService,
  TiktokEventRule,
  TiktokEventRuleInput,
  TiktokCrmEventType,
} from '@/services/tiktok.service';
import { Zap, Plus, Pencil, Trash2, Send, ArrowRight } from 'lucide-react';

interface PixelRow {
  pixel_id?: string;
  pixel_code?: string;
  name?: string;
  pixelId?: string;
  pixelCode?: string;
  pixelName?: string;
}

const CRM_EVENT_TYPES: Array<{ value: TiktokCrmEventType; label: string; hint: string; phrase: string }> = [
  { value: 'deal_won',         label: 'Deal won',          hint: 'For deals moved to a Won stage.',        phrase: 'a deal is moved to Won' },
  { value: 'deal_created',     label: 'Deal created',      hint: 'For newly added deals.',                  phrase: 'a new deal is created' },
  { value: 'lead_qualified',   label: 'Lead qualified',    hint: 'For leads marked qualified.',           phrase: 'a lead is marked qualified' },
  { value: 'lead_created',     label: 'Lead created',      hint: 'For newly arrived leads.',                   phrase: 'a new lead arrives' },
  { value: 'contact_created',  label: 'Contact created',   hint: 'For newly added contacts.',               phrase: 'a new contact is added' },
  { value: 'task_completed',   label: 'Task completed',    hint: 'For tasks marked done.',              phrase: 'a task is completed' },
  { value: 'manual',           label: 'Manual only',       hint: 'Sent only when you click Test.',       phrase: 'you press Test (never automatically)' },
];

const COMMON_TIKTOK_EVENTS = [
  'CompletePayment',
  'PlaceAnOrder',
  'AddToCart',
  'ViewContent',
  'ClickButton',
  'CompleteRegistration',
  'Search',
  'AddToWishlist',
  'Subscribe',
  'Contact',
];

const CURRENCIES = ['USD', 'AED', 'EUR', 'GBP', 'SAR', 'PKR', 'INR', 'EGP'];

function emptyForm(): TiktokEventRuleInput {
  return {
    name: '',
    crmEventType: 'deal_won',
    pixelCode: '',
    eventName: 'CompletePayment',
    valueField: '',
    currency: 'USD',
    isEnabled: true,
  };
}

/** Plain-language summary of what a rule does. */
function describeRule(r: Pick<TiktokEventRule, 'crmEventType' | 'eventName' | 'pixelCode' | 'valueField' | 'currency'>): string {
  const phrase = CRM_EVENT_TYPES.find((c) => c.value === r.crmEventType)?.phrase ?? r.crmEventType;
  const value = r.valueField ? `, with the value taken from ${r.valueField}${r.currency ? ` in ${r.currency}` : ''}` : '';
  return `Maps “${phrase}” to ${r.eventName || '…'} on pixel ${r.pixelCode || '…'}${value}. Sent when you press Test.`;
}

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function TiktokEventRules({ embedded = false }: Props = {}) {
  const [rules, setRules] = useState<TiktokEventRule[] | null>(null);
  const [pixels, setPixels] = useState<PixelRow[]>([]);
  const [editing, setEditing] = useState<TiktokEventRule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<TiktokEventRuleInput>(emptyForm());
  const [busy, setBusy] = useState(false);
  const [firingId, setFiringId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const refresh = () =>
    tiktokService.listEventRules().then(setRules).catch(() => setRules([]));

  useEffect(() => {
    refresh();
    tiktokService.listPixels().then((r) => setPixels(r as PixelRow[])).catch(() => setPixels([]));
  }, []);

  const pixelCodeOf = (p: PixelRow) => p.pixelCode ?? p.pixel_code ?? p.pixelId ?? p.pixel_id ?? '';
  const pixelNameOf = (p: PixelRow) => p.pixelName ?? p.name;

  const openCreate = () => {
    const firstPixel = pixels[0] ? pixelCodeOf(pixels[0]) : '';
    setForm({ ...emptyForm(), pixelCode: firstPixel });
    setEditing(null);
    setCreateOpen(true);
  };

  const openEdit = (r: TiktokEventRule) => {
    setEditing(r);
    setForm({
      name: r.name,
      crmEventType: r.crmEventType,
      pixelCode: r.pixelCode,
      eventName: r.eventName,
      valueField: r.valueField,
      currency: r.currency,
      isEnabled: r.isEnabled,
    });
    setCreateOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    if (!form.pixelCode) { toast.error('Pick a pixel'); return; }
    if (!form.eventName) { toast.error('Pick a TikTok event'); return; }
    setBusy(true);
    try {
      if (editing) {
        await tiktokService.updateEventRule(editing.ruleUid, form);
        toast.success('Rule updated');
      } else {
        await tiktokService.createEventRule(form);
        toast.success('Rule created');
      }
      setCreateOpen(false);
      setEditing(null);
      setForm(emptyForm());
      await refresh();
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setBusy(false); }
  };

  const remove = async (r: TiktokEventRule) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try {
      await tiktokService.deleteEventRule(r.ruleUid);
      toast.success('Rule deleted');
      await refresh();
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    }
  };

  const toggleEnabled = async (r: TiktokEventRule) => {
    setTogglingId(r.ruleUid);
    try {
      await tiktokService.updateEventRule(r.ruleUid, { isEnabled: !r.isEnabled });
      toast.success(`Rule ${r.isEnabled ? 'disabled' : 'enabled'}`);
      await refresh();
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setTogglingId(null); }
  };

  const fire = async (r: TiktokEventRule) => {
    setFiringId(r.ruleUid);
    try {
      const out = await tiktokService.fireEventRule(r.ruleUid, { source: 'manual_test', test: true });
      if (out.ok) toast.success('Test event sent — appears in TikTok Events Manager within minutes');
      else toast.error(out.message ?? 'Send failed');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setFiringId(null); }
  };

  const loading = rules === null;
  const hasData = !!rules && rules.length > 0;
  const enabledCount = (rules ?? []).filter((r) => r.isEnabled).length;

  return (
    <>
      {!embedded && (
        <Topbar
          title="Event Rules"
          breadcrumb="TikTok Ads / Event Rules"
          description="Map CRM events (deal_won, lead_qualified) to TikTok pixel events (CompletePayment, CompleteRegistration). Automatic forwarding is not active in this release — a rule sends a server-side event only when you press Test."
        />
      )}

      <TabBody>
        <TabToolbar
          title="Event rules"
          count={rules?.length ?? null}
          subtitle={hasData
            ? `${enabledCount} enabled · automatic forwarding is not active yet; press Test to send a rule's event to TikTok.`
            : 'Rules store a CRM → TikTok event mapping. Automatic forwarding is not active yet; press Test to send an event.'}
          right={<Button variant="primary" onClick={openCreate}><Plus size={14} /> New rule</Button>}
        />

        {loading && <SkeletonCards count={3} height={150} />}

        {!loading && !hasData && (
          <EmptyState
            icon={<Zap size={32} />}
            title="No event rules yet"
            description="Create a rule to map a CRM event (e.g. deal won) to a TikTok pixel event (e.g. CompletePayment). In this release events are sent only when you press Test — automatic conversion forwarding comes later."
            action={<Button variant="primary" onClick={openCreate}><Plus size={14} /> New rule</Button>}
          />
        )}

        {hasData && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rules!.map((r) => {
              const crm = CRM_EVENT_TYPES.find((c) => c.value === r.crmEventType);
              return (
                <Card key={r.ruleUid} hoverable className={clsx('flex flex-col gap-4 !p-4', !r.isEnabled && 'opacity-80')}>
                  <div className="flex items-start gap-3">
                    <span className={clsx(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                      r.isEnabled ? 'bg-primary-soft text-primary' : 'bg-surface-alt text-text-muted',
                    )}>
                      <Zap size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-text-primary">{r.name}</span>
                        <StatusPill
                          status={r.isEnabled ? 'active' : 'disabled'}
                          label={r.isEnabled ? 'Enabled' : 'Disabled'}
                          variant={r.isEnabled ? 'success' : 'neutral'}
                        />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                        <span className="rounded-md border border-border bg-surface-alt px-1.5 py-0.5 font-medium text-text-secondary">{crm?.label ?? r.crmEventType}</span>
                        <ArrowRight size={12} className="text-text-muted" />
                        <span className="rounded-md bg-primary-soft px-1.5 py-0.5 font-mono text-2xs font-medium text-primary">{r.eventName}</span>
                      </div>
                    </div>
                    <Switch
                      checked={r.isEnabled}
                      disabled={togglingId === r.ruleUid}
                      onChange={() => toggleEnabled(r)}
                      label={r.isEnabled ? 'Disable rule' : 'Enable rule'}
                    />
                  </div>

                  <p className="text-xs leading-relaxed text-text-muted">{describeRule(r)}</p>

                  <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
                    <span className="truncate font-mono text-2xs text-text-muted" title={`Pixel ${r.pixelCode}`}>
                      {r.pixelCode}{r.updatedAt ? ` · updated ${formatDateTime(r.updatedAt)}` : ''}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button size="sm" variant="secondary" onClick={() => fire(r)} loading={firingId === r.ruleUid} title="Send a test event using this rule">
                        <Send size={12} /> Test
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit rule" aria-label="Edit rule">
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} title="Delete rule" aria-label="Delete rule" className="hover:!bg-danger-soft hover:!text-danger">
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </TabBody>

      <Modal open={createOpen} onClose={() => !busy && setCreateOpen(false)} title={editing ? 'Edit rule' : 'New rule'} widthClass="max-w-lg">
        <div className="space-y-1">
          <FormField label="Rule name" required>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Deal won → Purchase" autoFocus />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="When this happens in the CRM" required hintBelow hint={CRM_EVENT_TYPES.find((c) => c.value === form.crmEventType)?.hint}>
              <Select value={form.crmEventType} onChange={(e) => setForm((f) => ({ ...f, crmEventType: e.target.value as TiktokCrmEventType }))}>
                {CRM_EVENT_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            </FormField>
            <FormField label="Send this TikTok event" required>
              <Select value={form.eventName} onChange={(e) => setForm((f) => ({ ...f, eventName: e.target.value }))}>
                {COMMON_TIKTOK_EVENTS.map((e) => <option key={e} value={e}>{e}</option>)}
              </Select>
            </FormField>
          </div>

          <FormField label="TikTok pixel" required>
            {pixels.length > 0 ? (
              <Select value={form.pixelCode} onChange={(e) => setForm((f) => ({ ...f, pixelCode: e.target.value }))}>
                <option value="">— Pick a pixel —</option>
                {pixels.map((p, i) => {
                  const code = pixelCodeOf(p);
                  return <option key={code || i} value={code}>{pixelNameOf(p) ?? code} · {code}</option>;
                })}
              </Select>
            ) : (
              <Input value={form.pixelCode} onChange={(e) => setForm((f) => ({ ...f, pixelCode: e.target.value }))} placeholder="C1234ABCDEFGHIJK" className="font-mono" />
            )}
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Value field" hintBelow hint="Dot-path into the event payload (e.g. deal.amount). Optional.">
              <Input value={form.valueField ?? ''} onChange={(e) => setForm((f) => ({ ...f, valueField: e.target.value }))} placeholder="deal.amount" className="font-mono" />
            </FormField>
            <FormField label="Currency" hintBelow hint="Used when the value field resolves.">
              <Select value={form.currency ?? 'USD'} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </FormField>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border bg-surface-alt/40 px-3 py-2.5">
            <div>
              <div className="text-[13px] font-medium text-text-secondary">Rule enabled</div>
              <div className="text-2xs text-text-muted">Disabled rules keep their settings. No rule sends automatically in this release.</div>
            </div>
            <Switch checked={form.isEnabled !== false} onChange={(v) => setForm((f) => ({ ...f, isEnabled: v }))} label="Rule enabled" />
          </div>

          <InlineHint className="mt-3">{describeRule(form)}</InlineHint>
        </div>
        <div className="-mx-4 -mb-4 mt-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={busy} disabled={busy || !form.name.trim() || !form.pixelCode}>
            {editing ? 'Save changes' : 'Create rule'}
          </Button>
        </div>
      </Modal>
    </>
  );
}
